import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useT } from '../i18n/i18n';
import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useChartTheme } from '../components/charts';
import {
  fetchProgress,
  shortDay,
  type DayPoint,
  type ProgressView,
} from '../progress/progress';

const WINDOWS = [7, 30, 90] as const;

function Chart({ view }: { view: ProgressView }) {
  const t = useT();
  const { colors, axis, tooltip, animate, duration } = useChartTheme();
  const days = [...new Set([...view.teaching, ...view.probes].map((d) => d.day))].sort();
  if (days.length === 0) return null;
  const teaching = new Map(view.teaching.map((d) => [d.day, d]));
  const probes = new Map(view.probes.map((d) => [d.day, d]));
  const data = days.map((day) => ({
    day,
    label: shortDay(day),
    teaching: teaching.get(day)?.percent ?? null,
    probe: probes.get(day)?.percent ?? null,
  }));
  const detail = (day: string, key: string): string => {
    const d = (key === 'probe' ? probes : teaching).get(day);
    if (!d) return '';
    return key === 'probe'
      ? t('{day} check on their own: {correct} of {trials}', { day: shortDay(day), correct: d.correct, trials: d.trials })
      : t('{day}: {correct} of {trials} right first time', { day: shortDay(day), correct: d.correct, trials: d.trials });
  };

  return (
    <div className="chart-wrap">
      <div role="img" aria-label={t('Right first time per day for {name}', { name: view.programName })} style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 24, right: 16, bottom: 4, left: 0 }}>
            <defs>
              <linearGradient id="progress-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.primary} stopOpacity={0.22} />
                <stop offset="100%" stopColor={colors.primary} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={colors.border} strokeDasharray="3 4" vertical={false} />
            <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={{ stroke: colors.border }} minTickGap={24} />
            <YAxis domain={[0, 110]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(v: number) => `${v}%`} tick={axis} tickLine={false} axisLine={false} width={48} />
            <Tooltip
              {...tooltip}
              formatter={(_v: number, key: string, item: { payload?: { day: string } }) => [detail(item.payload?.day ?? '', key), null]}
              labelFormatter={() => ''}
            />
            {view.mastery.on && (
              <ReferenceLine
                y={view.mastery.percentNeeded}
                stroke={colors.success}
                strokeDasharray="6 4"
                strokeWidth={2}
                label={{ value: t('learned at {pct}%', { pct: view.mastery.percentNeeded }), position: 'insideTopLeft', fill: colors.success, fontSize: 12, fontFamily: colors.font, fontWeight: 700 }}
              />
            )}
            <Area type="monotone" dataKey="teaching" stroke="none" fill="url(#progress-fill)" connectNulls isAnimationActive={animate} animationDuration={duration} />
            <Line type="monotone" dataKey="teaching" stroke={colors.primary} strokeWidth={3} connectNulls dot={{ r: 4, fill: colors.primary, stroke: colors.surface, strokeWidth: 2 }} activeDot={{ r: 7 }} isAnimationActive={animate} animationDuration={duration} />
            <Line type="monotone" dataKey="probe" stroke="none" dot={{ r: 6, fill: colors.surface, stroke: colors.ink, strokeWidth: 3 }} activeDot={{ r: 8 }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-key">
        <span><span className="chart-key-dot" aria-hidden="true" /> {t('Everyday practice')}</span>
        <span><span className="chart-key-dot is-probe" aria-hidden="true" /> {t('Checked on their own')}</span>
      </div>
    </div>
  );
}

function Summary({ view }: { view: ProgressView }) {
  const t = useT();
  const pct = view.totalTrials === 0
    ? 0
    : Math.round((view.totalCorrect / view.totalTrials) * 100);
  const lastProbe: DayPoint | undefined = view.probes[view.probes.length - 1];

  /* Weighted by the day's trials, not a mean of means: twenty answers on Monday and one on
     Tuesday is not a fifty-fifty split of the week. */
  const timedDays = view.teaching.filter((d) => d.averageMs !== null);
  const timedTrials = timedDays.reduce((n, d) => n + d.trials, 0);
  const timed = timedTrials === 0
    ? null
    : (timedDays.reduce((n, d) => n + (d.averageMs ?? 0) * d.trials, 0) / timedTrials / 1000).toFixed(1);

  const helpedTotal = view.teaching.reduce((n, d) => n + d.helped, 0);
  const helpedPct = view.totalTrials === 0 || helpedTotal === 0
    ? null
    : Math.round((helpedTotal / view.totalTrials) * 100);
  return (
    <div className="row">
      <div className="field">
        <span className="field-label">{t('Right first time')}</span>
        <strong className="figure">{pct}%</strong>
        <span className="field-hint">
          {t('{correct} of {trials} in the last {days} days', { correct: view.totalCorrect, trials: view.totalTrials, days: view.days })}
        </span>
      </div>
      <div className="field">
        <span className="field-label">{t('Days practised')}</span>
        <strong className="figure">{view.teaching.length}</strong>
      </div>
      {timed !== null && (
        <div className="field">
          <span className="field-label">{t('Time to answer')}</span>
          <strong className="figure">{t('{seconds}s', { seconds: timed })}</strong>
          <span className="field-hint">{t('Average across the period, timed on the device.')}</span>
        </div>
      )}
      {helpedPct !== null && (
        <div className="field">
          <span className="field-label">{t('Run with help')}</span>
          <strong className="figure">{helpedPct}%</strong>
          <span className="field-hint">{t('Trials where only the right tile responded.')}</span>
        </div>
      )}
      {lastProbe && (
        <div className="field">
          <span className="field-label">{t('Last check on their own')}</span>
          <strong className="figure">{lastProbe.percent}%</strong>
          <span className="field-hint">{t('{day}, with no help given', { day: shortDay(lastProbe.day) })}</span>
        </div>
      )}
    </div>
  );
}

export default function ProgressPage() {
  const t = useT();
  const { programId } = useParams();
  const [search] = useSearchParams();
  const [days, setDays] = useState<number>(Number(search.get('days')) || 30);
  const [view, setView] = useState<ProgressView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!programId) return;
    setLoading(true);
    setError(null);
    try {
      setView(await fetchProgress(programId, days));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not load this progress.'));
    } finally {
      setLoading(false);
    }
  }, [programId, days]);

  useEffect(() => { void load(); }, [load]);

  const nothingYet = view && view.teaching.length === 0 && view.probes.length === 0;

  return (
    <section aria-labelledby="progress-h">
      <div className="week-bar">
        <h2 id="progress-h" className="week-title">
          {view ? view.programName : t('Progress')}
        </h2>
        <div className="toolbar">
          {WINDOWS.map((w) => (
            <button
              key={w}
              className={`btn ${days === w ? '' : 'secondary'}`}
              onClick={() => setDays(w)}
            >
              {t('{n} days', { n: w })}
            </button>
          ))}
          <Link className="btn secondary" to="/">{t('Back')}</Link>
        </div>
      </div>

      {error && <div className="error" role="alert">{error}</div>}

      <div className="card">
        {loading && !view ? (
          <p className="muted">{t('Loading…')}</p>
        ) : nothingYet ? (
          <p className="muted">
            {t('Nothing recorded in this period yet. Play the program and the days will appear here.')}
          </p>
        ) : view ? (
          <>
            <Summary view={view} />
            <Chart view={view} />
            {view.mastery.on && (
              <div className="banner" role="status">
                {view.mastery.alreadyMaintenance
                  ? t('This one is on the maintenance list already.')
                  : view.mastery.met
                    ? view.mastery.daysNeeded === 1
                      ? t('Learned: {days} day running at {pct}% or better. It moves to the maintenance list when the next sitting ends.', { days: view.mastery.daysNeeded, pct: view.mastery.percentNeeded })
                      : t('Learned: {days} days running at {pct}% or better. It moves to the maintenance list when the next sitting ends.', { days: view.mastery.daysNeeded, pct: view.mastery.percentNeeded })
                    : t('{met} of {needed} days running at {pct}% or better. Only days with at least {min} tries count.', { met: view.mastery.daysMet, needed: view.mastery.daysNeeded, pct: view.mastery.percentNeeded, min: view.mastery.minTrialsPerDay })}
              </div>
            )}
            <p className="field-hint">
              {t('Each point is one day: how much the learner got right on the first try. A hollow point is a check taken with no help, which is the fairer measure of what they know.')}
            </p>
          </>
        ) : null}
      </div>
    </section>
  );
}
